import React from 'react';
import classNames from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { FormContentProps } from './FormContent.interfaces';
import { useStyles } from './FormContent.styles';
import { SectionHeading } from 'app/components/SectionHeading';
import { SectionContent } from 'app/components/SectionContent';
import { FormScreen } from 'app/components/FormScreen';
import { CommonClasses, FormsEnum, LabelEnum } from 'app/shared/enums';
import { DefaultFormActionButton } from 'app/components/DefaultFormActionButton';
import { FormScreenInterface } from 'app/shared/interfaces/FormScreen.interface';
import { FormPlaceholder } from 'app/components/FormPlaceholder';
import { DefaultFormFooter } from 'app/components/DefaultFormFooter';
import { LegalLinks } from 'app/components/LegalLinks';
import { onFormActionButtonClick } from 'app/store/thunks/onFormActionButtonClick';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';
import { selectIsCepik3PathActive } from 'app/store/selectors/selectIsCepik3PathActive';

export default function FormContent(props: FormContentProps) {
  const {
    handleSubmit,
    onSubmit,
    activeScreen,
    screens,
    actionButtonTitle,
    formName,
    SectionProps,
    isLoading,
    disabled,
    controlsList,
    renderActionButton: ActionButton = DefaultFormActionButton,
    renderFooter: Footer = DefaultFormFooter,
    renderScreenContentContainer: ScreenContainer = SectionContent,
    renderValidationMessage,
    renderFormRowContainer,
    renderSectionHeading,
    renderSection,
    newAmbassadorStyle,
  } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const classes = useStyles({ newAmbassadorStyle })();
  const isCepik3ShortPath = useSelector(selectIsCepik3PathActive);
  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);

  const isCalculationForm =
    formName === FormsEnum.CalculationForm || formName === FormsEnum.TravelForm;

  const isVehicleInformationUpdateAbTest = location.search.includes(
    'isVehicleInformationUpdate=true'
  );
  const isCalculationFormFirstStepForm =
    formName === FormsEnum.CalculationFirstStepForm;

  const actionButtonCurrentTitle =
    actionButtonTitle &&
    (activeScreen === screens.length - 1 ? actionButtonTitle : LabelEnum.Next);

  const renderHeading = (screen: FormScreenInterface) => {
    return screen.label ? (
      <SectionHeading className={classes.heading} title={screen.label} />
    ) : null;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {screens.length === 0 ? (
        <FormPlaceholder />
      ) : (
        <fieldset disabled={isLoading} className={classes.fieldset}>
          {screens.map(
            (screen, i) =>
              activeScreen >= i && (
                <div
                  className={`formScreen-${i} ${classes.formScreen} ${
                    props?.classes?.formScreen || ''
                  }`}
                  key={screen.key}
                >
                  {renderHeading(screen)}
                  <ScreenContainer className={props?.classes?.sectionContent}>
                    <FormScreen
                      SectionProps={SectionProps}
                      screenKey={screen.key}
                      label={screen.label}
                      formName={formName}
                      controlsList={controlsList}
                      renderValidationMessage={renderValidationMessage}
                      renderFormRowContainer={renderFormRowContainer}
                      renderSectionHeading={renderSectionHeading}
                      renderSection={renderSection}
                      isLoadingResult={isLoading}
                    />
                  </ScreenContainer>
                </div>
              )
          )}
        </fieldset>
      )}
      {actionButtonCurrentTitle && (
        <Footer isPaymentView={formName === FormsEnum.CheckoutPaymentForm}>
          <div
            className={classNames({
              [classes.footer]:
                isCalculationForm ||
                isVehicleInformationUpdateAbTest ||
                isCalculationFormFirstStepForm,
            })}
          >
            {(isCalculationForm ||
              (isCalculationFormFirstStepForm && isCepik3ShortPath)) &&
              !isFormMicroCenterActive && <LegalLinks />}

            <ActionButton
              isLoading={isLoading}
              disabled={disabled}
              className={classNames(
                CommonClasses.ActionButton,
                props?.classes?.[CommonClasses.ActionButton]
              )}
              title={actionButtonCurrentTitle}
              onClick={() =>
                dispatch(onFormActionButtonClick({ formName, activeScreen }))
              }
            />
          </div>
        </Footer>
      )}
    </form>
  );
}
